import { useState, useRef } from "react";
import { api, resolveUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Upload, ImageIcon } from "lucide-react";

const EMPTY = {
  brand_name: "",
  description: "",
  website_url: "",
  logo_url: "",
  accent_color: "#292524",
  is_visible: true,
  display_order: 0,
};

export function BrandFormDialog({ open, onOpenChange, brand, onSaved }) {
  const [form, setForm] = useState(brand ? { ...EMPTY, ...brand } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!brand;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/admin/upload-logo", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_url", res.data.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.brand_name.trim()) {
      toast.error("Brand name is required");
      return;
    }
    setSaving(true);
    const payload = {
      brand_name: form.brand_name,
      description: form.description,
      website_url: form.website_url,
      logo_url: form.logo_url,
      accent_color: form.accent_color,
      is_visible: form.is_visible,
      display_order: Number(form.display_order) || 0,
    };
    try {
      if (isEdit) {
        await api.put(`/admin/brands/${brand.id}`, payload);
        toast.success("Brand updated");
      } else {
        await api.post("/admin/brands", payload);
        toast.success("Brand added");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="brand-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit brand" : "Add new brand"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Brand name</Label>
            <Input
              value={form.brand_name}
              onChange={(e) => set("brand_name", e.target.value)}
              placeholder="Cloud Accounting Support Services"
              data-testid="brand-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Short description</Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What this brand does…"
              data-testid="brand-description-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Website URL</Label>
            <Input
              value={form.website_url}
              onChange={(e) => set("website_url", e.target.value)}
              placeholder="https://www.example.co.uk"
              data-testid="brand-website-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Brand logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                {form.logo_url ? (
                  <img
                    src={resolveUrl(form.logo_url)}
                    alt="Logo preview"
                    className="h-full w-full object-contain p-2 mix-blend-multiply"
                    data-testid="logo-preview"
                  />
                ) : (
                  <ImageIcon className="h-6 w-6 text-stone-300" />
                )}
              </div>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleUpload}
                  data-testid="logo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="logo-upload-button"
                >
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {form.logo_url ? "Replace logo" : "Upload logo"}
                </Button>
                <p className="mt-1.5 text-xs text-stone-400">PNG, JPG, SVG or WEBP · max 5MB</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Accent colour</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-stone-200"
                  data-testid="brand-accent-input"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Display order</Label>
              <Input
                type="number"
                value={form.display_order}
                onChange={(e) => set("display_order", e.target.value)}
                data-testid="brand-order-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-stone-200 px-4 py-3">
            <div>
              <Label>Visible on website</Label>
              <p className="text-xs text-stone-400">Hidden brands are removed from the public site.</p>
            </div>
            <Switch
              checked={form.is_visible}
              onCheckedChange={(v) => set("is_visible", v)}
              data-testid="brand-visible-switch"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="brand-cancel-button">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-lg" data-testid="brand-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Add brand"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
